import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import {
  AdminActivitiesControllerDeleteActivity200Response,
  AdminActivitiesControllerGetActivityDetail200Response,
  AdminActivitiesControllerGetActivityParticipants200Response,
  AdminActivitiesControllerListActivities200Response,
  AdminActivitiesControllerGetActivitiesKpis200Response,
  AdminActivityDetailUserDto,
  AdminActivityTableItemDto,
  AdminService,
  GetAdminActivitiesKpiResponse,
  GetAdminActivityDetailResponse,
  GetAdminActivityParticipantsResponse,
  ListActivitiesRequestParams,
  ListAdminActivitiesResponse,
  SportDefinitionResume,
  SportsControllerGetActiveSports200Response,
  SportsService
} from '@smatchy/sdk';
import type {
  ActivitiesListQuery,
  ActivitiesSummary,
  ActivityStatus,
  EditGroupActivity,
  EditGroupMember,
  PaginatedResponse,
  SportTypeConfig
} from '../../presentations/models/activities-management.model';
import {
  formatActivityDateLabel,
  formatDurationFromNumber,
  formatTime24,
  formatTimeRange24
} from '../../presentations/utils/date.util';
import { environment } from '../../../environments/environment';

type AdminActivityEditApiResponse = {
  error?: string | object | null;
  value?: {
    updated?: boolean;
  } | null;
};

type AdminActivityCancelApiResponse = {
  error?: string | object | null;
  value?: {
    cancelled?: boolean;
  } | null;
  cancelled?: boolean;
};

export type CancellationReasonLine = {
  reason: string;
  count: number;
  withAWord: number;
  sharePercent: number;
};

export type CancellationReasons = {
  total: number;
  lines: CancellationReasonLine[];
  mostFrequent: string | null;
  withAWord: number;
};

type CancellationReasonsApiResponse = (CancellationReasons & { value?: CancellationReasons }) | null;

export type ActivityParticipantsSection = {
  data: EditGroupMember[];
  total: number;
  page: number;
  limit: number;
};

@Injectable({ providedIn: 'root' })
export class ActivitiesManagementApiService {
  constructor(
    private http: HttpClient,
    private adminService: AdminService,
    private sportsService: SportsService
  ) {}

  getSummary(): Observable<ActivitiesSummary> {
    return this.adminService.getActivitiesKpis().pipe(
      map(response => this.mapKpiResponse(response))
    );
  }

  list(query: ActivitiesListQuery): Observable<PaginatedResponse<EditGroupActivity>> {
    return this.adminService.listActivities(this.toListRequestParams(query)).pipe(
      map(response => this.mapListResponse(response))
    );
  }

  getSportTypes(): Observable<SportTypeConfig[]> {
    return this.sportsService.getActiveSports({ lang: 'en' }).pipe(
      map(response => this.mapSportTypesResponse(response))
    );
  }

  getDetail(activityId: string): Observable<EditGroupActivity> {
    return this.adminService.getActivityDetail({ activityId }).pipe(
      map(response => this.mapDetailResponse(response))
    );
  }

  getParticipants(activityId: string, page = 1, limit = 10): Observable<ActivityParticipantsSection> {
    return this.adminService.getActivityParticipants({ activityId, page, limit }).pipe(
      map(response => this.mapParticipantsResponse(response))
    );
  }

  updateActivity(
    activityId: string,
    draft: EditGroupActivity,
    sportId: string,
    level: string
  ): Observable<void> {
    const body = new FormData();
    body.append('title', draft.name.trim());
    body.append('description', draft.description?.trim() ?? '');
    if (draft.routeUrl?.trim()) {
      body.append('routeUrl', draft.routeUrl.trim());
    } else if (draft.detailLoaded) {
      body.append('removeRouteUrl', 'true');
    }
    body.append('sportId', sportId);
    body.append('acceptAnyLvl', String(level === 'all'));

    if (level !== 'all') {
      body.append('level', level);
    }

    if (draft.backgroundImageFile) {
      body.append('backgroundImage', draft.backgroundImageFile);
    } else if (draft.backgroundImageRemoved) {
      body.append('backgroundImage', '');
    }

    return this.http.patch<AdminActivityEditApiResponse>(
      `${this.getApiBasePath()}/api/v1/admin/activities/${encodeURIComponent(activityId)}`,
      body
    ).pipe(
      map(response => {
        if (response?.error) {
          throw response.error;
        }
      })
    );
  }

  cancelActivity(activityId: string): Observable<void> {
    return this.http.patch<AdminActivityCancelApiResponse>(
      `${this.getApiBasePath()}/api/v1/admin/activities/${encodeURIComponent(activityId)}/cancel`,
      {}
    ).pipe(
      map(response => {
        if (response?.error) {
          throw response.error;
        }
      })
    );
  }

  /**
   * Why the activities of a period were cancelled, for the doughnut above the
   * table.
   *
   * Hand-written rather than taken from the generated SDK: the route is newer
   * than the last generation. The period is read by the API on the activity
   * start date, not on the moment somebody cancelled, because nothing records
   * that moment.
   */
  getCancellationReasons(range: { startDate?: string; endDate?: string } = {}): Observable<CancellationReasons> {
    let params = new HttpParams();
    if (range.startDate) {
      params = params.set('startDate', range.startDate);
    }
    if (range.endDate) {
      params = params.set('endDate', range.endDate);
    }

    return this.http
      .get<CancellationReasonsApiResponse>(`${this.getApiBasePath()}/api/v1/admin/activities/cancellation-reasons`, {
        params,
      })
      .pipe(
        map(response => {
          const value = response?.value ?? response;
          return {
            total: value?.total ?? 0,
            lines: value?.lines ?? [],
            mostFrequent: value?.mostFrequent ?? null,
            withAWord: value?.withAWord ?? 0,
          };
        })
      );
  }

  deleteActivity(activityId: string): Observable<void> {
    return this.adminService.deleteActivity({ activityId }).pipe(
      map((response: AdminActivitiesControllerDeleteActivity200Response) => {
        if (response && 'error' in response && response.error) {
          throw response.error;
        }
      })
    );
  }

  /**
   * Opens the CSV the API builds, carrying the filters currently applied.
   *
   * Goes through HttpClient and a blob rather than pointing the browser at the
   * URL: the endpoint needs the bearer token, which a plain navigation would
   * not send.
   */
  exportCsv(query: ActivitiesListQuery): Observable<Blob> {
    const requestParams = this.toListRequestParams(query) as Record<string, unknown>;
    const params = Object.keys(requestParams).reduce((acc, key) => {
      const value = requestParams[key];
      return value === undefined || value === null || value === '' ? acc : acc.set(key, String(value));
    }, new HttpParams());

    return this.http.get(`${environment.api}/api/v1/admin/activities/export/csv`, {
      params,
      responseType: 'blob',
    });
  }

  private toListRequestParams(query: ActivitiesListQuery): ListActivitiesRequestParams {
    const search = query.search?.trim();
    const status = this.toApiStatus(query.status);

    return {
      search: search || undefined,
      status,
      startDate: query.startDate || undefined,
      endDate: query.endDate || undefined,
      userId: query.userId?.trim() || undefined,
      sportId: query.sportId || undefined,
      location: query.location?.trim() || undefined,
      page: query.page,
      limit: query.pageSize,
      sortBy: this.toApiSortField(query.sortField),
      sortOrder: query.sortOrder,
      // Quick-chip flags the generated SDK does not know yet.
      ...({
        withoutPartners: query.withoutPartners ? 'true' : undefined,
        recurring: query.recurring ? 'true' : undefined,
        full: query.full ? 'true' : undefined,
      } as object)
    };
  }

  private toApiStatus(status?: ActivityStatus | 'all'): ListActivitiesRequestParams['status'] {
    switch (status) {
      case 'incoming':
        return 'scheduled';
      case 'completed':
        return 'finished';
      case 'canceled':
        return 'cancelled';
      default:
        return undefined;
    }
  }

  private toApiSortField(
    sortField?: ActivitiesListQuery['sortField']
  ): ListActivitiesRequestParams['sortBy'] {
    return sortField === 'name' ? 'activity' : sortField;
  }

  private mapListResponse(
    response: AdminActivitiesControllerListActivities200Response | ListAdminActivitiesResponse
  ): PaginatedResponse<EditGroupActivity> {
    if ('error' in response && response.error) {
      throw response.error;
    }

    const value = 'data' in response ? response : response.value;

    return {
      data: value?.data.map(activity => this.mapActivityRow(activity)) ?? [],
      total: value?.total ?? 0,
      page: value?.page ?? 1,
      pageSize: value?.limit ?? 10
    };
  }

  private mapActivityRow(activity: AdminActivityTableItemDto): EditGroupActivity {
    const apiActivity = activity as AdminActivityTableItemDto & {
      sport?: { id: string; name: string; icon: string } | null;
      activity?: { ownerId?: string };
      privacy?: 'public' | 'private' | 'members_only';
      type?: string;
    };

    return {
      id: activity.id,
      name: activity.activity.name,
      time: formatTime24(activity.schedule.startDate),
      date: formatActivityDateLabel(activity.schedule.startDate),
      status: this.toUiStatus(activity.status),
      groupId: activity.group.id,
      groupName: activity.group.name,
      startDateIso: activity.schedule.startDate,
      endDateIso: activity.schedule.endDate,
      recurrence: activity.schedule.recurrence === 'weekly' ? 'weekly' : 'once',
      thumbnailUrl: activity.activity.thumbnailUrl ?? '',
      sport: apiActivity.sport?.name ?? '',
      sportId: apiActivity.sport?.id,
      sportIcon: apiActivity.sport?.icon ?? '',
      location: activity.locationName ?? '',
      host: activity.activity.ownerName,
      ownerId: apiActivity.activity?.ownerId,
      participants: activity.participantsCount,
      type: apiActivity.type,
      maxAttendees: (activity as AdminActivityTableItemDto & { maxAttendees?: number }).maxAttendees,
      privacy: apiActivity.privacy,
      cancellationReason: (activity as AdminActivityTableItemDto & { cancellationReason?: string })
        .cancellationReason,
      cancellationReasonText: (
        activity as AdminActivityTableItemDto & { cancellationReasonText?: string }
      ).cancellationReasonText
    };
  }

  private mapDetailResponse(
    response: AdminActivitiesControllerGetActivityDetail200Response | GetAdminActivityDetailResponse
  ): EditGroupActivity {
    if ('error' in response && response.error) {
      throw response.error;
    }

    const value = 'id' in response ? response : response.value;
    if (!value) {
      throw new Error('Activity detail response is empty.');
    }

    const participantData = value.participants.data ?? [];
    const participantsTotal = value.participants.total ?? participantData.length;
    const participantDetails = participantData.map(participant =>
      this.mapDetailParticipant(participant)
    );
    const level = value.level.acceptAnyLvl || value.level.value == null
      ? 'All levels'
      : `Level ${value.level.value}`;

    return {
      id: value.id,
      detailLoaded: true,
      name: value.title,
      createdAtIso: value.createdAt,
      time: formatTimeRange24(value.schedule.startDate, value.schedule.endDate),
      date: formatActivityDateLabel(value.schedule.startDate),
      groupId: value.group?.id,
      groupName: value.group?.name,
      startDateIso: value.schedule.startDate,
      endDateIso: value.schedule.endDate,
      recurrence: value.schedule.recurrence === 'weekly' ? 'weekly' : 'once',
      thumbnailUrl: value.backgroundUrl ?? '',
      sport: value.sport.name,
      sportId: value.sport.id,
      sportIcon: value.sport.icon,
      location: '',
      host: value.creator.displayName,
      hostEmail: value.creator.email,
      hostAvatarUrl: value.creator.avatarUrl,
      participants: participantsTotal,
      participantDetails,
      activityType: formatDurationFromNumber(value.schedule.duration),
      durationSeconds: value.schedule.duration,
      level,
      description: value.description,
      routeUrl: value.routeUrl,
      handiFriendly: value.handiFriendly ?? false,
      status: this.toUiStatus(value.status),
      type: value.type,
      privacy: value.privacy
    };
  }

  private mapDetailParticipant(participant: AdminActivityDetailUserDto): EditGroupMember {
    return {
      name: participant.displayName,
      email: participant.email,
      avatarUrl: participant.avatarUrl
    };
  }

  private mapParticipantsResponse(
    response:
      | AdminActivitiesControllerGetActivityParticipants200Response
      | GetAdminActivityParticipantsResponse
  ): ActivityParticipantsSection {
    if ('error' in response && response.error) {
      throw response.error;
    }

    const value = 'data' in response
      ? response
      : response.value;
    if (!value) {
      throw new Error('Activity participants response is empty.');
    }

    return {
      data: (value.data ?? []).map(participant =>
        this.mapDetailParticipant(participant)
      ),
      total: value.total ?? 0,
      page: value.page ?? 1,
      limit: value.limit ?? 10
    };
  }

  private toUiStatus(status: AdminActivityTableItemDto['status']): ActivityStatus {
    switch (String(status).trim().toLowerCase()) {
      case 'scheduled':
        return 'incoming';
      case 'finished':
        return 'completed';
      case 'cancelled':
      case 'deleted':
        return 'canceled';
      default:
        return 'incoming';
    }
  }

  private mapKpiResponse(
    response: AdminActivitiesControllerGetActivitiesKpis200Response | GetAdminActivitiesKpiResponse
  ): ActivitiesSummary {
    if ('error' in response && response.error) {
      throw response.error;
    }

    const value = 'totalActivities' in response ? response : response.value;
    // The generated SDK lags behind the API; the new figures ride as an extension.
    const extendedKpi = value as (typeof value) & {
      partnerSeats?: number;
      partnerSeatsGrowthPct?: number | null;
      createdGrowthPct?: number | null;
      upcomingWithoutPartners?: number;
    };

    return {
      totalActivities: value?.totalActivities ?? 0,
      activitiesCreatedThisMonth: value?.activitiesCreatedThisMonth ?? 0,
      totalParticipants: value?.totalParticipants ?? 0,
      incoming: value?.incoming ?? 0,
      completionRate: value?.completionRate ?? 0,
      completionDelta: value?.completionRateDelta ?? 0,
      partnerSeats: extendedKpi?.partnerSeats ?? 0,
      partnerSeatsGrowthPct: extendedKpi?.partnerSeatsGrowthPct ?? null,
      createdGrowthPct: extendedKpi?.createdGrowthPct ?? null,
      upcomingWithoutPartners: extendedKpi?.upcomingWithoutPartners ?? 0
    };
  }

  private mapSportTypesResponse(response: SportsControllerGetActiveSports200Response): SportTypeConfig[] {
    if (response.error) {
      throw response.error;
    }

    return (response.value?.sports ?? [])
      .map(sport => this.mapSportTypeConfig(sport))
      .sort((left, right) => left.label.localeCompare(right.label));
  }

  private mapSportTypeConfig(sport: SportDefinitionResume): SportTypeConfig {
    return {
      label: sport.name,
      value: sport._id || sport.id,
      icon: sport.icon || sport.id,
      maxLevel: sport.maxLevel
    };
  }

  private getApiBasePath(): string {
    return (this.adminService.configuration.basePath ?? '').replace(/\/$/, '');
  }
}
